import React, { useEffect } from "react";
import styled from "styled-components";
import { X } from "lucide-react";

interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
}

interface DialogContentProps {
  children: React.ReactNode;
  className?: string;
}

interface DialogHeaderProps {
  children: React.ReactNode;
}

interface DialogTitleProps {
  children: React.ReactNode;
}

interface DialogFooterProps {
  children: React.ReactNode;
}

export const Dialog: React.FC<DialogProps> = ({
  open,
  onOpenChange,
  children,
}) => {
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [open]);

  if (!open) return null;

  return (
    <DialogOverlay onClick={() => onOpenChange(false)}>
      {children}
    </DialogOverlay>
  );
};

export const DialogContent: React.FC<DialogContentProps> = ({
  children,
  className,
}) => {
  return (
    <DialogContentWrapper
      className={className}
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </DialogContentWrapper>
  );
};

export const DialogHeader: React.FC<DialogHeaderProps> = ({ children }) => {
  return <DialogHeaderWrapper>{children}</DialogHeaderWrapper>;
};

export const DialogTitle: React.FC<DialogTitleProps> = ({ children }) => {
  return <DialogTitleWrapper>{children}</DialogTitleWrapper>;
};

export const DialogFooter: React.FC<DialogFooterProps> = ({ children }) => {
  return <DialogFooterWrapper>{children}</DialogFooterWrapper>;
};

export const DialogClose: React.FC<{ onClick: () => void }> = ({ onClick }) => {
  return (
    <DialogCloseButton onClick={onClick} aria-label="Close dialog">
      <X size={20} />
    </DialogCloseButton>
  );
};

const DialogOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 50;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  animation: fadeIn 0.2s ease-in-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const DialogContentWrapper = styled.div`
  position: relative;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  width: 100%;
  max-width: 800px;
  max-height: 85vh;
  overflow-y: auto;
  animation: slideUp 0.2s ease-in-out;

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media screen and (max-width: 768px) {
    max-width: 100%;
    max-height: 90vh;
    border-radius: 12px 12px 0 0;
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    animation: slideUpMobile 0.3s ease-out;
  }

  @keyframes slideUpMobile {
    from {
      transform: translateY(100%);
    }
    to {
      transform: translateY(0);
    }
  }
`;

const DialogHeaderWrapper = styled.div`
  padding: 24px 24px 16px 24px;
  border-bottom: 1px solid #e2e2e2;
  position: relative;

  @media screen and (max-width: 500px) {
    padding: 20px 20px 12px 20px;
  }
`;

const DialogTitleWrapper = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #373744;
  margin: 0;
  padding-right: 40px;

  @media screen and (max-width: 500px) {
    font-size: 18px;
  }
`;

const DialogFooterWrapper = styled.div`
  padding: 16px 24px 24px 24px;
  border-top: 1px solid #e2e2e2;
  display: flex;
  justify-content: flex-end;
  gap: 12px;

  @media screen and (max-width: 500px) {
    padding: 12px 20px 20px 20px;
  }
`;

const DialogCloseButton = styled.button`
  position: absolute;
  right: 24px;
  top: 24px;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  border: 1px solid #e2e2e2;
  background-color: white;
  color: #666;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: #f5f5f5;
    border-color: #d3d3d3;
  }

  &:focus-visible {
    outline: 2px solid var(--blue-300);
    outline-offset: 2px;
  }

  @media screen and (max-width: 500px) {
    right: 20px;
    top: 20px;
  }
`;
